import { ONBOARDING_PATHS } from './paths';

export type OnboardingPageId = 'index' | 'verification' | 'get-started' | 'wizard';

/** The account's onboarding position, as every routing decision reads it. */
export type OnboardingState = {
  /** Trial, non-builder, flag on — everyone onboarding applies to. */
  isEligible: boolean;
  /** Also asked for automated workflows at signup, so the checklist suits them. */
  isChecklistCohort: boolean;
  isComplete: boolean;
  isVerified: boolean;
  hasAnswered: boolean;
  /** Without it the wizard falls back to the static get-started question. */
  isAiEnabled: boolean;
};

export type OnboardingRedirectInput = OnboardingState & {
  page: OnboardingPageId;
};

/**
 * Where an `/onboarding/*` page should send the user, or `null` to stay.
 * Pure so the whole gate matrix is unit-tested once; pages render a
 * `SafeNavigate` off the answer.
 *
 * - Ineligible accounts and accounts that already finished onboarding in any
 *   workspace skip the flow entirely and land on home (`/` forwards there).
 * - The wizard owns answered users when AI is on, so a return visit replays
 *   the recorded conversation instead of bouncing. Forward navigation is the
 *   page's own one-shot move.
 * - Everywhere else, users who already answered are done with `/onboarding` —
 *   home's checklist is their next step.
 * - Everyone else moves on to the wizard, verified or not: verification never
 *   blocks onboarding. The gate page stays usable for an unverified visitor,
 *   but nothing routes to it, and a verified visitor is moved along.
 * - Without AI there is no wizard, so the checklist cohort falls back to its
 *   static question and everyone else, who has no checklist waiting for them
 *   either, is sent on into the app. get-started stays directly reachable
 *   there — but with AI on the wizard owns onboarding, so get-started
 *   forwards into it. Its lone platform answer does not finish the wizard's
 *   question set, so a user left on it would save and go nowhere.
 */
export function selectOnboardingRedirect(input: OnboardingRedirectInput): string | null {
  const { page, isVerified, isEligible, isChecklistCohort, isComplete, hasAnswered, isAiEnabled } = input;

  if (!isEligible || isComplete) {
    return '/';
  }
  const withoutAi = isChecklistCohort ? ONBOARDING_PATHS.getStarted : '/';
  if (page === 'wizard') {
    if (isAiEnabled) {
      return null;
    }
    return hasAnswered ? '/' : withoutAi;
  }
  if (hasAnswered) {
    return '/';
  }
  if (page === 'verification' && !isVerified) {
    return null;
  }
  if (page === 'get-started') {
    return isAiEnabled ? ONBOARDING_PATHS.wizard : null;
  }
  return isAiEnabled ? ONBOARDING_PATHS.wizard : withoutAi;
}
